export type AdminOperationId =
  | 'provider-imports'
  | 'flight-statistics'
  | 'all-flight-statistics'
  | 'glider-statistics'
  | 'takeoff-altitudes'
  | 'takeoff-countries'
  | 'flight-tracks'
  | 'job-history-expiry'
  | 'event-expiry'

export type AdminOperation = {
  id: AdminOperationId
  title: string
  description: string
  path: string
  flightSpecific?: boolean
  cutoffDateSpecific?: boolean
}

export const ADMIN_OPERATIONS: AdminOperation[] = [
  {
    id: 'provider-imports',
    title: 'Syride and XContest imports',
    description: 'Queue incremental imports for every user with a linked provider account.',
    path: '/v0/admin/schedule-provider-imports',
  },
  {
    id: 'all-flight-statistics',
    title: 'All flight statistics',
    description: 'Recompute statistics and scores for every flight with stored IGC data.',
    path: '/v0/admin/recompute-statistics',
  },
  {
    id: 'flight-statistics',
    title: 'Single flight statistics',
    description: 'Recompute statistics and scores for one flight.',
    path: '/v0/admin/recompute-statistics/{id}',
    flightSpecific: true,
  },
  {
    id: 'glider-statistics',
    title: 'Glider statistics',
    description: 'Recompute flight counts and airtime for every glider.',
    path: '/v0/admin/recompute-glider-statistics',
  },
  {
    id: 'takeoff-altitudes',
    title: 'Takeoff altitudes',
    description: 'Fill missing takeoff altitudes from the configured elevation data.',
    path: '/v0/admin/backfill-altitudes',
  },
  {
    id: 'takeoff-countries',
    title: 'Takeoff countries',
    description: 'Fill takeoff country codes from their coordinates.',
    path: '/v0/admin/backfill-takeoff-countries',
  },
  {
    id: 'flight-tracks',
    title: 'Flight tracks',
    description: 'Extract legacy inline tracks into media and recompute JSON track data.',
    path: '/v0/admin/backfill-tracks',
  },
  {
    id: 'job-history-expiry',
    title: 'Job run history',
    description: 'Delete completed, cancelled, and discarded job runs finalized before a selected date.',
    path: '/v0/admin/expire-job-history',
    cutoffDateSpecific: true,
  },
  {
    id: 'event-expiry',
    title: 'Ephemeral events',
    description: 'Delete ephemeral operational events older than one week.',
    path: '/v0/admin/expire-events',
  },
]

export function adminOperationPath(operation: AdminOperation, flightId?: number) {
  if (!operation.flightSpecific) return operation.path
  if (!Number.isInteger(flightId) || (flightId ?? 0) <= 0) {
    throw new Error('A positive flight ID is required')
  }
  return operation.path.replace('{id}', String(flightId))
}

type Fetcher = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>

export async function enqueueAdminOperation(
  fetcher: Fetcher,
  operation: AdminOperation,
  flightId?: number,
  cutoffDate?: string,
) {
  const init: RequestInit = { method: 'POST' }
  if (operation.cutoffDateSpecific) {
    if (!cutoffDate) throw new Error('A cutoff date is required')
    init.headers = { 'Content-Type': 'application/json' }
    init.body = JSON.stringify({ cutoff_date: cutoffDate })
  }
  const response = await fetcher(adminOperationPath(operation, flightId), init)
  if (!response.ok) {
    const body = await response.json().catch(() => null)
    throw new Error(body?.detail || body?.title || `Failed to queue ${operation.title}`)
  }
  const body = await response.json().catch(() => null)
  return body?.status || 'queued'
}
